import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Layers, Box, Tag, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ProductModelDialog } from "@/components/configuration/ProductModelDialog";
import { ComponentTypeDialog } from "@/components/configuration/ComponentTypeDialog";
import { AttributeDialog } from "@/components/configuration/AttributeDialog";
import type { Tables, Enums } from "@/integrations/supabase/types";

type ProductModel = Tables<"product_models">;
type ComponentType = Tables<"component_types">;
type Attribute = Tables<"attributes">;
type AttributeType = Enums<"attribute_type">;

const attributeTypeLabels: Record<AttributeType, string> = {
  text: "Teksti",
  number: "Numero",
  date: "Päivämäärä",
  selection: "Valinta",
  coordinates: "Koordinaatit",
};

export default function ProductConfiguration() {
  const queryClient = useQueryClient();
  const [selectedModel, setSelectedModel] = useState<ProductModel | null>(null);
  const [selectedComponentType, setSelectedComponentType] = useState<ComponentType | null>(null);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(null);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [componentDialogOpen, setComponentDialogOpen] = useState(false);
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);

  // Fetch product models
  const { data: productModels = [], isLoading: loadingModels, isError: modelsError } = useQuery({
    queryKey: ["product_models"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_models")
        .select("*")
        .is("deleted_at", null)
        .order("position");
      if (error) throw error;
      return data;
    },
  });

  // Fetch component types
  const { data: componentTypes = [], isLoading: loadingTypes, isError: typesError } = useQuery({
    queryKey: ["component_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("component_types")
        .select("*")
        .is("deleted_at", null)
        .order("position");
      if (error) throw error;
      return data;
    },
  });

  // Fetch attributes
  const { data: attributes = [], isLoading: loadingAttributes, isError: attributesError } = useQuery({
    queryKey: ["attributes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attributes")
        .select("*")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Delete product model (soft delete)
  const deleteModelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("product_models")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_models"] });
      toast.success("Tuotemalli poistettu");
    },
    onError: () => toast.error("Poisto epäonnistui"),
  });

  // Delete component type (soft delete)
  const deleteTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("component_types")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["component_types"] });
      toast.success("Komponenttityyppi poistettu");
    },
    onError: () => toast.error("Poisto epäonnistui"),
  });

  // Delete attribute (soft delete)
  const deleteAttributeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("attributes")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
      toast.success("Attribuutti poistettu");
    },
    onError: () => toast.error("Poisto epäonnistui"),
  });

  const handleEditModel = (model: ProductModel) => {
    setSelectedModel(model);
    setModelDialogOpen(true);
  };

  const handleNewModel = () => {
    setSelectedModel(null);
    setModelDialogOpen(true);
  };

  const handleEditComponentType = (type: ComponentType) => {
    setSelectedComponentType(type);
    setComponentDialogOpen(true);
  };

  const handleNewComponentType = () => {
    setSelectedComponentType(null);
    setComponentDialogOpen(true);
  };

  const handleEditAttribute = (attr: Attribute) => {
    setSelectedAttribute(attr);
    setAttributeDialogOpen(true);
  };

  const handleNewAttribute = () => {
    setSelectedAttribute(null);
    setAttributeDialogOpen(true);
  };

  if (modelsError || typesError || attributesError) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold">Tietojen lataus epäonnistui</h2>
          <p className="text-muted-foreground">Yritä ladata sivu uudelleen.</p>
          <Button onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Lataa uudelleen
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Tuotemallien hallinta</h1>
          <p className="text-muted-foreground">
            Hallinnoi tuotemalleja, komponenttityyppejä ja attribuutteja
          </p>
        </div>

        <Tabs defaultValue="models">
          <TabsList>
            <TabsTrigger value="models" className="gap-2">
              <Layers className="h-4 w-4" />
              Tuotemallit
            </TabsTrigger>
            <TabsTrigger value="components" className="gap-2">
              <Box className="h-4 w-4" />
              Komponenttityypit
            </TabsTrigger>
            <TabsTrigger value="attributes" className="gap-2">
              <Tag className="h-4 w-4" />
              Attribuutit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="models" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Tuotemallit</CardTitle>
                <Button onClick={handleNewModel} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Uusi tuotemalli
                </Button>
              </CardHeader>
              <CardContent>
                {loadingModels ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ladataan...
                  </div>
                ) : productModels.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ei tuotemalleja. Luo ensimmäinen tuotemalli.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nimi</TableHead>
                        <TableHead>Kuvaus</TableHead>
                        <TableHead>Tila</TableHead>
                        <TableHead className="w-[100px]">Toiminnot</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productModels.map((model) => (
                        <TableRow key={model.id}>
                          <TableCell className="font-medium">{model.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {model.description || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={model.is_active ? "default" : "secondary"}>
                              {model.is_active ? "Aktiivinen" : "Ei käytössä"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditModel(model)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteModelMutation.mutate(model.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="components" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Komponenttityypit</CardTitle>
                <Button onClick={handleNewComponentType} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Uusi komponenttityyppi
                </Button>
              </CardHeader>
              <CardContent>
                {loadingTypes ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ladataan...
                  </div>
                ) : componentTypes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ei komponenttityyppejä. Luo ensimmäinen komponenttityyppi.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nimi</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Mittamoodi</TableHead>
                        <TableHead>Pakollinen</TableHead>
                        <TableHead>Useita sallittu</TableHead>
                        <TableHead className="w-[100px]">Toiminnot</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {componentTypes.map((type) => (
                        <TableRow key={type.id}>
                          <TableCell className="font-medium">{type.name}</TableCell>
                          <TableCell>{type.label}</TableCell>
                          <TableCell>
                            <Badge variant={
                              type.dimension_mode === "catalog" ? "default" :
                              type.dimension_mode === "none" ? "secondary" : "outline"
                            }>
                              {type.dimension_mode === "catalog" ? "Katalogi" :
                               type.dimension_mode === "none" ? "Ei mittoja" : "Vapaa"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={type.is_mandatory ? "default" : "outline"}>
                              {type.is_mandatory ? "Kyllä" : "Ei"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={type.allow_multiple ? "default" : "outline"}>
                              {type.allow_multiple ? "Kyllä" : "Ei"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditComponentType(type)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteTypeMutation.mutate(type.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attributes" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Attribuutit</CardTitle>
                <Button onClick={handleNewAttribute} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Uusi attribuutti
                </Button>
              </CardHeader>
              <CardContent>
                {loadingAttributes ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ladataan...
                  </div>
                ) : attributes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Ei attribuutteja. Luo ensimmäinen attribuutti.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nimi</TableHead>
                        <TableHead>Näyttönimi</TableHead>
                        <TableHead>Tyyppi</TableHead>
                        <TableHead>Pakollinen</TableHead>
                        <TableHead className="w-[100px]">Toiminnot</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attributes.map((attr) => (
                        <TableRow key={attr.id}>
                          <TableCell className="font-medium font-mono text-sm">
                            {attr.name}
                          </TableCell>
                          <TableCell>{attr.label}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {attributeTypeLabels[attr.attribute_type]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={attr.is_required ? "default" : "outline"}>
                              {attr.is_required ? "Kyllä" : "Ei"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditAttribute(attr)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteAttributeMutation.mutate(attr.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <ProductModelDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        model={selectedModel}
      />

      <ComponentTypeDialog
        open={componentDialogOpen}
        onOpenChange={setComponentDialogOpen}
        componentType={selectedComponentType}
      />

      <AttributeDialog
        open={attributeDialogOpen}
        onOpenChange={setAttributeDialogOpen}
        attribute={selectedAttribute}
      />
    </Layout>
  );
}
